import { DatePicker, Form, Input, Select } from "antd";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import CarDetailEditFieldTable from "../../../cars/components/cardetails/CarDetailEditFieldTable";
import { FORM_INPUT_CLASS, FORM_ITEM_CLASS } from "../../../cars/components/cardetails/carDetailClasses";

/** Every IANA zone the browser knows; customers can live anywhere, not only in the US. */
const listTimeZones = () => {
    try {
        if (typeof Intl.supportedValuesOf === "function") return Intl.supportedValuesOf("timeZone");
    } catch {
        // Older browsers — fall through.
    }
    return ["America/New_York", "America/Chicago", "America/Denver", "America/Phoenix", "America/Los_Angeles", "America/Anchorage", "Pacific/Honolulu", "UTC"];
};

function CustomerDetailEditForm({ form, onFinish, t }) {
    const rows = [
        {
            label: t("firstName"),
            content: (
                <Form.Item
                    name="firstName"
                    className={FORM_ITEM_CLASS}
                    rules={[{ required: true, message: t("customerDetail.emptyFieldsError") }]}>
                    <Input className={FORM_INPUT_CLASS} />
                </Form.Item>
            ),
        },
        {
            label: t("lastName"),
            content: (
                <Form.Item
                    name="lastName"
                    className={FORM_ITEM_CLASS}
                    rules={[{ required: true, message: t("customerDetail.emptyFieldsError") }]}>
                    <Input className={FORM_INPUT_CLASS} />
                </Form.Item>
            ),
        },
        {
            label: t("customerDetail.dateOfBirth"),
            content: (
                <Form.Item
                    name="dateOfBirth"
                    className={FORM_ITEM_CLASS}
                    rules={[{ required: true, message: t("customerDetail.emptyFieldsError") }]}>
                    <DatePicker className="w-full" format="YYYY-MM-DD" />
                </Form.Item>
            ),
        },
        {
            label: t("customerDetail.contactNo"),
            content: (
                <Form.Item
                    name="mobileNumber"
                    className={FORM_ITEM_CLASS}
                    rules={[{ required: true, message: t("customerDetail.emptyFieldsError") }]}>
                    <Input className={FORM_INPUT_CLASS} />
                </Form.Item>
            ),
        },
        {
            label: t("email"),
            content: (
                <Form.Item
                    name="email"
                    className={FORM_ITEM_CLASS}
                    rules={[
                        { required: true, message: t("customerDetail.emptyFieldsError") },
                        { type: "email", message: t("customerDetail.emptyFieldsError") },
                    ]}>
                    <Input className={FORM_INPUT_CLASS} />
                </Form.Item>
            ),
        },
        {
            // Display-only preference the customer can also set from their booking-site profile.
            label: t("customerDetail.timezone"),
            content: (
                <Form.Item name="timezone" className={FORM_ITEM_CLASS}>
                    <Select
                        showSearch
                        optionFilterProp="label"
                        className="w-full"
                        options={[
                            { value: "", label: t("customerDetail.timezoneAuto") },
                            ...listTimeZones().map((zone) => ({ value: zone, label: zone.replace(/_/g, " ") })),
                        ]}
                    />
                </Form.Item>
            ),
        },
        {
            label: t("address"),
            content: (
                <Form.Item
                    name="address"
                    className={FORM_ITEM_CLASS}
                    rules={[{ required: true, message: t("customerDetail.emptyFieldsError") }]}>
                    <Input.TextArea rows={2} className={FORM_INPUT_CLASS} />
                </Form.Item>
            ),
        },
    ];

    return (
        <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            initialValues={{
                dateOfBirth: undefined,
            }}>
            <CarDetailEditFieldTable title={t("personalInfo")} rows={rows} />
        </Form>
    );
}

CustomerDetailEditForm.propTypes = {
    form: PropTypes.object.isRequired,
    onFinish: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
};

export function customerToFormValues(customer) {
    if (!customer) return {};
    return {
        firstName: customer.firstName || "",
        lastName: customer.lastName || "",
        email: customer.email || "",
        mobileNumber: customer.mobileNumber || "",
        address: customer.address || "",
        timezone: customer.timezone || "",
        dateOfBirth: customer.dateOfBirth ? dayjs(customer.dateOfBirth) : null,
    };
}

export default CustomerDetailEditForm;
